import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { Spacings, Fonts } from '../../../../styleConstants';
import DotsBg from '../DotsBg';

const CardWrapper = styled.div`
  position: relative;
`;

const MIN_CARD_HEIGHT = 244;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_12B};
  text-align: left;
  color: ${({ $textColor }) => $textColor};
  background-color: ${({ $bgColor }) => $bgColor};
  box-shadow: ${boxShadowGenerator({
    xOffset: -`${Spacings.SPACING_4B}`,
    yOffset: Spacings.SPACING_4B,
    blur: Spacings.SPACING_6B,
    color: 'rgba(220, 239, 253, 0.2)'
  })};
  border-radius: ${Spacings.SPACING_5B};
  height: ${MIN_CARD_HEIGHT}px;
  transition: height 500ms ease-out;
  &:hover {
    height: ${({ $expandedHeight }) => `${$expandedHeight}px`};
  }
  overflow: hidden;
  cursor: pointer;
`;

const CardBackground = styled(DotsBg)`
  position: absolute;
  top: -${Spacings.SPACING_3B};
  left: -${Spacings.SPACING_11B};
  opacity: 0.2;
`;

const EventImg = styled.img`
  position: absolute;
  bottom: -${Spacings.SPACING_6B};
  right: -${Spacings.SPACING_8B};
  z-index: 2;
`;

const CardLabel = styled.div`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H2};
  line-height: ${Spacings.SPACING_10B};
  height: 120px;
`;

const EventInfoWrapper = styled.div`
  position: relative;
  margin-top: ${Spacings.SPACING_6B};
`;

const EventInfo = styled.div`
  font-weight: ${Fonts.REGULAR};
  font-size: ${Fonts.H3};
  line-height: ${Spacings.SPACING_9B};
  opacity: ${({ $opacity }) => $opacity};
  transition: opacity 300ms ease-out;
  position: absolute;
  top: 0px;
  left: 0px;
`;

const EventCard = ({
  title,
  textColor,
  bgColor,
  imageUrl,
  eventsInfo,
  description
}) => {
  const [isInHover, setIsInHover] = useState(false);
  const onMouseEnter = () => {
    setIsInHover(true);
  };

  const onMouseLeave = () => {
    setIsInHover(false);
  };

  const descriptionRef = useRef();
  const expandedHeight = useRef(0);

  useEffect(() => {
    // min card height - events info height + description height
    expandedHeight.current =
      MIN_CARD_HEIGHT + descriptionRef.current.getBoundingClientRect().height;
  }, []);

  return (
    <CardWrapper>
      <CardBackground color={textColor} />
      <Card
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        $bgColor={bgColor}
        $textColor={textColor}
        $expandedHeight={expandedHeight.current}
      >
        <CardLabel>{title}</CardLabel>
        <EventInfoWrapper>
          <EventInfo $opacity={!isInHover ? '1' : '0'}>{eventsInfo}</EventInfo>
          <EventInfo ref={descriptionRef} $opacity={isInHover ? '1' : '0'}>
            {description}
          </EventInfo>
        </EventInfoWrapper>
      </Card>
      <EventImg src={imageUrl} />
    </CardWrapper>
  );
};

EventCard.propTypes = {
  title: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  bgColor: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired,
  eventsInfo: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired
};

export default EventCard;
